import { useEffect, useState } from 'react'
import { Loader2 } from '@/lib/icons'
import type { RejectReason as RejectReasonType } from '@ayub/shared'
import { REJECT_REASON_LABELS, RejectReason } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'

/**
 * `RejectReason` blogerlar VA videolar uchun umumiy enum (`@ayub/shared`), shuning uchun unda
 * video-ga xos sabablar ham bor (masalan "Kitob ko'rinmayapti", "Havola ochilmadi"). Bloger
 * arizasini rad etishda faqat mantiqan mos keladiganlarini ko'rsatamiz — xuddi
 * `docs/03-app-flow.md` "Yangi arizani ko'rish tartibi"da sanalganidek: ozchilik obunachi /
 * yopiq profil / mos kelmaydigan kontent / soxta akkaunt / boshqa. Matnning o'zi baribir
 * `REJECT_REASON_LABELS`dan olinadi — bu yerda hech narsa qayta yozilmaydi.
 *
 * Bu — FAQAT standart (bloger) ro'yxati. Boshqa chaqiruvchilar (masalan videolar kartochkasi,
 * Bosqich 7) o'z sabablar to'plamini `reasons` prop orqali beradi — komponentning o'zi
 * ikkalasi uchun ham umumiy, faqat ro'yxat farq qiladi.
 */
const BLOGGER_REJECT_REASONS: readonly RejectReasonType[] = [
  RejectReason.LOW_FOLLOWERS,
  RejectReason.PRIVATE_PROFILE,
  RejectReason.IRRELEVANT_CONTENT,
  RejectReason.FAKE_ACCOUNT,
  RejectReason.OTHER,
]

export interface RejectReasonPickerProps {
  open: boolean
  isSubmitting?: boolean
  /** Ko'rsatiladigan sabablar ro'yxati — berilmasa bloger sabablariga tushadi. */
  reasons?: readonly RejectReasonType[]
  onCancel: () => void
  onSubmit: (reason: RejectReasonType, note: string | undefined) => void
}

/**
 * Rad etish sababini tanlash oynasi. `OTHER` tanlanganda izoh maydoni MAJBURIY —
 * bu serverning 422'siga tayanmasdan, shu yerda tekshiriladi (tugma o'zi bloklanadi).
 */
export function RejectReasonPicker({
  open,
  isSubmitting = false,
  reasons = BLOGGER_REJECT_REASONS,
  onCancel,
  onSubmit,
}: RejectReasonPickerProps) {
  const [reason, setReason] = useState<RejectReasonType | null>(null)
  const [note, setNote] = useState('')

  // Har safar qayta ochilganda oldingi tanlov qolib ketmasin.
  useEffect(() => {
    if (open) {
      setReason(null)
      setNote('')
    }
  }, [open])

  const isOther = reason === RejectReason.OTHER
  const isValid = reason !== null && (!isOther || note.trim().length > 0)

  function handleSubmit() {
    if (!isValid || reason === null) return
    onSubmit(reason, note.trim().length > 0 ? note.trim() : undefined)
  }

  return (
    <Modal open={open} onClose={onCancel} ariaLabel="Rad etish sababi" size="compact" zIndex={50}>
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">Rad etish sababi</h2>

        <div role="radiogroup" aria-label="Rad etish sababi" className="flex flex-col gap-1.5">
          {reasons.map((value) => (
            <label
              key={value}
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-button border px-3 py-2 text-sm transition-colors duration-150 ease-out',
                reason === value
                  ? 'border-brand bg-brand-subtle text-brand'
                  : 'border-app-border hover:bg-app-bg',
              )}
            >
              <input
                type="radio"
                name="reject-reason"
                value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
                className="size-4 accent-brand"
              />
              {REJECT_REASON_LABELS[value]}
            </label>
          ))}
        </div>

        {isOther && (
          <div className="flex flex-col gap-1">
            <label htmlFor="reject-note" className="text-xs font-medium text-app-hint">
              Sababni yozing (majburiy)
            </label>
            <textarea
              id="reject-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              placeholder="Masalan: profilida kitob mavzusiga oid kontent yo'q"
              className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Bekor qilish
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            Rad etish
          </Button>
        </div>
      </div>
    </Modal>
  )
}
